// src/Pages/FAQ/HtmlToolbar.tsx
import React, { useEffect, useRef, useState } from "react";
import { Paper, Toolbar, Tooltip, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered, ContentPaste as ParagraphIcon, KeyboardReturn as BrIcon, AddLink as AddLinkIcon } from "@mui/icons-material";

let lastFocusedTA: HTMLTextAreaElement | null = null;

function useTrackTextareaFocus() {
  const installed = useRef(false);
  useEffect(() => {
    if (installed.current) return;
    const onFocusIn = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.tagName === "TEXTAREA") lastFocusedTA = t as HTMLTextAreaElement;
    };
    document.addEventListener("focusin", onFocusIn, true);
    installed.current = true;
    return () => document.removeEventListener("focusin", onFocusIn, true);
  }, []);
}

// 優先找工具列下方最近的 textarea
function findLocalTextarea(root: HTMLElement | null): HTMLTextAreaElement | null {
  if (!root) return null;
  let n: Element | null = root;
  while ((n = n.nextElementSibling)) {
    if (n.tagName === "TEXTAREA") return n as HTMLTextAreaElement;
    const q = n.querySelector("textarea");
    if (q) return q as HTMLTextAreaElement;
  }
  const parent = root.parentElement;
  if (parent) {
    const inParent = parent.querySelector("textarea");
    if (inParent) return inParent as HTMLTextAreaElement;
  }
  return null;
}

function getVisibleOrNull(ta: HTMLTextAreaElement | null) {
  if (!ta) return null;
  if (!document.contains(ta)) return null;
  if (ta.disabled) return null;
  if (ta.offsetParent === null) return null;
  if (getComputedStyle(ta).visibility === "hidden") return null;
  return ta;
}

function insertAtSelectionTA(ta: HTMLTextAreaElement, snippet: string, caretPos?: number) {
  const start = ta.selectionStart ?? 0;
  const end = ta.selectionEnd ?? 0;
  const before = ta.value.slice(0, start);
  const after = ta.value.slice(end);
  ta.value = before + snippet + after;
  ta.dispatchEvent(new Event("input", { bubbles: true }));
  requestAnimationFrame(() => {
    ta.focus();
    const pos = typeof caretPos === "number" ? caretPos : before.length + snippet.length;
    ta.setSelectionRange(pos, pos);
  });
}

function wrapSelectionWithTagTA(ta: HTMLTextAreaElement, tag: "b" | "i" | "a", attrs: Record<string, string> = {}, fallbackText?: string) {
  const start = ta.selectionStart ?? 0;
  const end = ta.selectionEnd ?? 0;
  const before = ta.value.slice(0, start);
  const sel = ta.value.slice(start, end);
  const after = ta.value.slice(end);

  const attrsStr = Object.keys(attrs).length
    ? " " +
      Object.entries(attrs)
        .map(([k, v]) => `${k}="${v}"`)
        .join(" ")
    : "";

  const inner = sel || (typeof fallbackText === "string" ? fallbackText : "");
  const open = `<${tag}${attrsStr}>`;
  const close = `</${tag}>`;

  ta.value = before + open + inner + close + after;
  ta.dispatchEvent(new Event("input", { bubbles: true }));

  requestAnimationFrame(() => {
    ta.focus();
    const caret = before.length + open.length + (sel ? sel.length : 0);
    ta.setSelectionRange(caret, caret);
  });
}

const HtmlToolbar: React.FC = () => {
  useTrackTextareaFocus();

  const rootRef = useRef<HTMLDivElement | null>(null);

  // Link dialog state
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState("https://");
  const [linkText, setLinkText] = useState("");
  const [blank, setBlank] = useState(true);

  // 鎖定 link 動作時的目標與選取
  const linkTargetRef = useRef<HTMLTextAreaElement | null>(null);
  const linkSelRef = useRef<{ start: number; end: number } | null>(null);

  // 關閉後要執行的「延後插入請求」
  const [pendingLink, setPendingLink] = useState<null | {
    href: string;
    text: string;
    blank: boolean;
    // 盡量復原當時的 selection；若 DOM 變動，仍會插在當前 caret
    start: number;
    end: number;
  }>(null);

  const pickTA = () =>
    getVisibleOrNull(findLocalTextarea(rootRef.current)) ||
    getVisibleOrNull(lastFocusedTA) ||
    getVisibleOrNull((Array.from(document.querySelectorAll("textarea")) as HTMLTextAreaElement[])[0]) ||
    null;

  const stopFocus = (e: React.MouseEvent) => e.preventDefault();

  // === Buttons ===
  const onBold = () => {
    const ta = pickTA();
    if (ta) wrapSelectionWithTagTA(ta, "b");
  };
  const onItalic = () => {
    const ta = pickTA();
    if (ta) wrapSelectionWithTagTA(ta, "i");
  };
  const onParagraph = () => {
    const ta = pickTA();
    if (!ta) return;
    const open = "<p>";
    const close = "</p>";
    const start = ta.selectionStart ?? 0;
    insertAtSelectionTA(ta, open + close, start + open.length);
  };
  const onBr = () => {
    const ta = pickTA();
    if (!ta) return;
    const insert = "<br>";
    const start = ta.selectionStart ?? 0;
    insertAtSelectionTA(ta, insert, start + insert.length);
  };
  const onUl = () => {
    const ta = pickTA();
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const sel = ta.value.slice(start, end);
    if (sel.trim()) {
      const li = sel
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((t) => `<li>${t}</li>`)
        .join("");
      const html = `<ul>${li}</ul>`;
      insertAtSelectionTA(ta, html, start + html.length);
    } else {
      const pre = `<ul><li>`;
      const post = `</li></ul>`;
      insertAtSelectionTA(ta, pre + post, start + pre.length);
    }
  };
  const onOl = () => {
    const ta = pickTA();
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const sel = ta.value.slice(start, end);
    if (sel.trim()) {
      const li = sel
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((t) => `<li>${t}</li>`)
        .join("");
      const html = `<ol>${li}</ol>`;
      insertAtSelectionTA(ta, html, start + html.length);
    } else {
      const pre = `<ol><li>`;
      const post = `</li></ol>`;
      insertAtSelectionTA(ta, pre + post, start + pre.length);
    }
  };

  // --- Link Dialog ---
  const openLinkDlg = () => {
    const ta = pickTA();
    if (!ta) return;
    linkTargetRef.current = ta;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    linkSelRef.current = { start, end };
    setLinkText(ta.value.slice(start, end) || "");
    setLinkHref("https://");
    setBlank(true);
    setLinkOpen(true);
  };

  // 重要：不要在 Dialog 打開時直接插入；先關閉，再延後插入
  const confirmLink = () => {
    const ta = linkTargetRef.current ?? pickTA();
    if (!ta) {
      setLinkOpen(false);
      return;
    }
    const sel = linkSelRef.current ?? { start: ta.selectionStart ?? 0, end: ta.selectionEnd ?? 0 };
    setPendingLink({
      href: linkHref,
      text: linkText || "",
      blank,
      start: sel.start,
      end: sel.end,
    });
    setLinkOpen(false); // 關閉，解除 focus trap
  };

  const closeLinkDlg = () => {
    setLinkOpen(false);
  };

  // 對話框關閉後執行延後插入（此時已沒有 focus trap，DOM 也穩定了）
  useEffect(() => {
    if (!linkOpen && pendingLink) {
      const ta =
        linkTargetRef.current || // 優先用原本的
        pickTA(); // 退而求其次用目前策略挑選
      if (ta) {
        requestAnimationFrame(() => {
          try {
            ta.focus();
            ta.setSelectionRange(pendingLink.start, pendingLink.end);
          } catch {}
          const attrs: Record<string, string> = { href: pendingLink.href };
          if (pendingLink.blank) {
            attrs.target = "_blank";
            attrs.rel = "noopener";
          }
          wrapSelectionWithTagTA(ta, "a", attrs, pendingLink.text);
        });
      }
      // 清理暫存
      setPendingLink(null);
      linkTargetRef.current = null;
      linkSelRef.current = null;
    }
  }, [linkOpen, pendingLink]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Paper ref={rootRef} elevation={1} sx={{ mb: 1, borderRadius: 2, overflow: "hidden" }}>
        <Toolbar variant="dense" sx={{ gap: 0.5, flexWrap: "wrap" }}>
          <Tooltip title="Bold <b>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onBold}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic <i>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onItalic}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

          <Tooltip title="Unordered list <ul>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onUl}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ordered list <ol>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onOl}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

          <Tooltip title="Paragraph <p>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onParagraph}>
              <ParagraphIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Line break <br>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={onBr}>
              <BrIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

          <Tooltip title="Link <a>">
            <IconButton size="small" onMouseDown={stopFocus} onClick={openLinkDlg}>
              <AddLinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </Paper>

      <Dialog open={linkOpen} onClose={closeLinkDlg} fullWidth maxWidth="sm">
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField label="Link text (optional)" fullWidth margin="dense" value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="(leave blank to insert empty link body)" />
          <TextField label="URL" fullWidth margin="dense" type="url" placeholder="https://example.com" value={linkHref} onChange={(e) => setLinkHref(e.target.value)} />
          <Button size="small" sx={{ mt: 1 }} variant={blank ? "contained" : "outlined"} onClick={() => setBlank((s) => !s)}>
            {blank ? "Open in new tab ✓" : "Open in same tab"}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkDlg}>Cancel</Button>
          <Button variant="contained" onClick={confirmLink}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HtmlToolbar;
